import React, { useEffect, useState } from 'react';
import { Check, Pencil, X, ArrowLeftRight, MessageCircle, Sparkles, Send, Undo2, Landmark } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Celda "Detalle / Cliente" de una SALIDA del ledger 1TouchPay.
 *
 * - Detalle: texto libre editable siempre (guarda con Enter o con el tilde).
 * - Cliente: lo resuelve el backend en `_detalle_salida`. Segun el origen:
 *     interno    -> traspaso entre nuestras cuentas, no hay cliente
 *     whatsapp   -> pago self-service, ya venia atribuido
 *     pago_crm   -> se eligio el cliente al hacer el pago
 *     confirmado -> alguien lo confirmo a mano
 *     sugerido   -> deducido por el CBU destino: se muestra como SUGERENCIA a confirmar
 */
export const LedgerSalidaDetalle = ({ mov, clientes = [], token, onSaved }) => {
  const info = mov._detalle_salida || {};
  const [editando, setEditando] = useState(false);
  const [nota, setNota] = useState(info.nota || '');
  const [guardando, setGuardando] = useState(false);
  const [eligiendo, setEligiendo] = useState(false);
  const [clienteElegido, setClienteElegido] = useState('');
  const [errorGuardado, setErrorGuardado] = useState(null);
  const [eligiendoOp, setEligiendoOp] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);
  useEffect(() => { if (!editando) setNota(info.nota || ''); }, [info.nota, editando]);

  const money = (n) => `$${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;
  const yaImputado = !!mov.compartido_receipt_id;

  // Manda el comprobante al grupo del cliente. El negativo se agenda una sola
  // vez: si la salida ya impactó en el dashboard, el backend sólo reenvía.
  const compartir = async () => {
    const aviso = yaImputado
      ? `Reenviar el comprobante de ${money(mov.importe)} a ${info.cliente_nombre}?\n\nEl saldo NO se toca (ya está imputado).`
      : `Enviar el comprobante de ${money(mov.importe)} a ${info.cliente_nombre} y descontarlo de su saldo?`;
    if (!window.confirm(aviso)) return;
    setEnviando(true);
    setResultado(null);
    try {
      const res = await fetch(`${API_URL}/api/bancos/1touchpay/ledger/compartir`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ otp_id: mov.otp_id, cliente_id: info.cliente_id }),
      });
      const data = await res.json();
      if (!res.ok) { setResultado({ error: data.detail || 'No se pudo enviar' }); return; }
      setResultado(data);
      await onSaved?.({ otpId: mov.otp_id, kind: 'compartir' });
    } catch (e) {
      setResultado({ error: 'Error de red' });
    } finally { setEnviando(false); }
  };

  const deshacer = async () => {
    if (!window.confirm(`Deshacer la imputación de ${money(mov.importe)}?\n\nSe borra el movimiento del dashboard y se devuelve el saldo. El comprobante ya enviado no se puede borrar.`)) return;
    setEnviando(true);
    try {
      const res = await fetch(`${API_URL}/api/bancos/1touchpay/ledger/compartir/deshacer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ otp_id: mov.otp_id }),
      });
      const data = await res.json();
      setResultado(res.ok ? { deshecho: true, ...data } : { error: data.detail || 'No se pudo deshacer' });
      if (res.ok) await onSaved?.({ otpId: mov.otp_id, kind: 'deshacer' });
    } catch (e) {
      setResultado({ error: 'Error de red' });
    } finally { setEnviando(false); }
  };

  const post = async (path, payload) => {
    setGuardando(true);
    setErrorGuardado(null);
    try {
      const res = await fetch(`${API_URL}/api/bancos/1touchpay/ledger/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ otp_id: mov.otp_id, ...payload }),
      });
      if (res.ok) { await onSaved?.({ otpId: mov.otp_id, kind: path }); return true; }
      const data = await res.json().catch(() => ({}));
      setErrorGuardado(typeof data.detail === 'string' ? data.detail : 'No se pudo guardar. Intentá de nuevo.');
    } catch (e) { setErrorGuardado('No se pudo guardar. Revisá la conexión e intentá de nuevo.'); }
    finally { setGuardando(false); }
    return false;
  };

  const guardarNota = async () => {
    if (nota === (info.nota || '')) { setEditando(false); return; }
    if (await post('nota', { nota })) setEditando(false);
  };

  const badge = {
    interno: { txt: 'Traspaso interno', cls: 'bg-slate-100 text-slate-700', Icon: ArrowLeftRight },
    whatsapp: { txt: 'WhatsApp', cls: 'bg-green-100 text-green-800', Icon: MessageCircle },
    pago_crm: { txt: 'Pago CRM', cls: 'bg-blue-100 text-blue-800', Icon: Check },
    confirmado: { txt: 'Confirmado', cls: 'bg-emerald-100 text-emerald-800', Icon: Check },
    sugerido: { txt: 'Sugerido', cls: 'bg-amber-100 text-amber-800', Icon: Sparkles },
  }[info.origen];

  return (
    <div className="flex flex-col gap-1 min-w-[190px]" data-testid={`ledger-salida-cell-${mov.otp_id}`}>
      {/* ── Cliente ── */}
      <div className="flex items-center gap-1 flex-wrap">
        {badge && (
          <span className={`inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded font-semibold ${badge.cls}`}
            data-testid={`ledger-salida-origen-${mov.otp_id}`}>
            <badge.Icon className="w-2.5 h-2.5" /> {badge.txt}
          </span>
        )}
        {info.cliente_nombre && (
          <span className="text-[10px] font-medium text-gray-800 dark:text-gray-200 truncate max-w-[120px]"
            title={info.cliente_nombre} data-testid={`ledger-salida-cliente-${mov.otp_id}`}>
            {info.cliente_nombre}
          </span>
        )}
        {info.sugerido && !yaImputado && !eligiendo && (
          <button onClick={() => post('atribuir-cliente', { cliente_id: info.cliente_id })} disabled={guardando || enviando}
            className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-medium disabled:opacity-50 transition-colors"
            title="Confirmar que esta salida fue de este cliente"
            data-testid={`ledger-salida-confirmar-${mov.otp_id}`}>
            {guardando ? '…' : 'Confirmar'}
          </button>
        )}
        {(!info.cliente_nombre || info.sugerido) && info.origen !== 'interno' && !yaImputado && !eligiendo && (
          <button onClick={() => { setClienteElegido(info.cliente_id || ''); setErrorGuardado(null); setResultado(null); setEligiendo(true); }}
            disabled={guardando || enviando}
            className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium disabled:opacity-50 transition-colors"
            title={info.sugerido ? 'Cambiar el cliente sugerido' : 'Asignar cliente'}
            data-testid={`ledger-salida-${info.sugerido ? 'cambiar' : 'asignar'}-${mov.otp_id}`}>
            {info.sugerido ? <><Pencil className="w-2.5 h-2.5" /> Cambiar cliente</> : '+ Cliente'}
          </button>
        )}
        {eligiendo && (
          <div className="flex items-center gap-1 flex-wrap min-w-0 w-full">
            <select autoFocus value={clienteElegido} disabled={guardando || enviando}
              onChange={(e) => { setClienteElegido(e.target.value); setErrorGuardado(null); }}
              aria-label="Cliente de la salida"
              className="text-[10px] px-1 py-0.5 border border-gray-300 rounded bg-white dark:bg-gray-800 dark:text-gray-200 max-w-[150px]"
              data-testid={`ledger-salida-select-${mov.otp_id}`}>
              <option value="">{clientes.length ? 'Elegir cliente…' : 'Sin clientes disponibles'}</option>
              {clientes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button onClick={async () => { if (await post('atribuir-cliente', { cliente_id: clienteElegido })) setEligiendo(false); }}
              disabled={guardando || enviando || !clientes.some((c) => c.id === clienteElegido)}
              className="text-emerald-600 hover:text-emerald-800 disabled:opacity-50 transition-colors"
              title="Guardar cliente" aria-label="Guardar cliente"
              data-testid={`ledger-salida-guardar-cliente-${mov.otp_id}`}>
              <Check className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => { setEligiendo(false); setErrorGuardado(null); }} disabled={guardando || enviando}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50 transition-colors"
              title="Cancelar cambio de cliente" aria-label="Cancelar cambio de cliente"
              data-testid={`ledger-salida-cancelar-cliente-${mov.otp_id}`}>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
      {errorGuardado && (
        <span role="alert" className="text-[10px] text-red-600 break-words"
          data-testid={`ledger-salida-error-${mov.otp_id}`}>{errorGuardado}</span>
      )}

      {/* ── Compartir al cliente + imputar ── */}
      {info.cliente_id && info.origen !== 'interno' && (
        <div className="flex items-center gap-1 flex-wrap">
          <button onClick={compartir} disabled={enviando || guardando || eligiendo}
            className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white font-medium disabled:opacity-50 transition-colors"
            title={yaImputado
              ? 'Reenviar el comprobante al grupo del cliente (el saldo ya está imputado)'
              : 'Mandar el comprobante al grupo del cliente y descontarlo de su saldo'}
            data-testid={`ledger-compartir-btn-${mov.otp_id}`}>
            <Send className="w-2.5 h-2.5" /> {enviando ? '…' : (yaImputado ? 'Reenviar' : 'Compartir e imputar')}
          </button>
          {yaImputado && (
            <button onClick={deshacer} disabled={enviando || guardando || eligiendo}
              className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium disabled:opacity-50 transition-colors"
              title="Borrar el movimiento del dashboard y devolver el saldo"
              data-testid={`ledger-deshacer-btn-${mov.otp_id}`}>
              <Undo2 className="w-2.5 h-2.5" /> Deshacer
            </button>
          )}
          {resultado && (
            <span className={`text-[9px] font-medium ${resultado.error ? 'text-red-600' : 'text-emerald-700'}`}
              data-testid={`ledger-compartir-result-${mov.otp_id}`}>
              {resultado.error ? resultado.error
                : resultado.deshecho ? `Saldo devuelto · nuevo ${money(resultado.new_balance)}`
                : `${Object.entries(resultado.canales || {}).map(([k, v]) => `${k}:${v === 'ok' ? '✓' : '✕'}`).join(' ')}`
                  + (resultado.imputado ? ` · imputado (saldo ${money(resultado.new_balance)})` : ' · ya estaba imputado')}
            </span>
          )}
        </div>
      )}

      {/* ── Operación (salidas genéricas: pago a la unión, gastos) ── */}
      <div className="flex items-center gap-1 flex-wrap">
        {info.operacion_id ? (
          <>
            <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded font-semibold bg-violet-100 text-violet-800"
              title={`Imputada al libro ${info.operacion_nombre}. No afecta el saldo de ningún cliente.`}
              data-testid={`ledger-operacion-${mov.otp_id}`}>
              <Landmark className="w-2.5 h-2.5" /> {info.operacion_nombre || info.operacion_id}
            </span>
            <button onClick={() => post('atribuir-operacion', { operacion: null })} disabled={guardando}
              className="text-[9px] text-gray-400 hover:text-red-600 disabled:opacity-50"
              title="Quitar la imputación al libro"
              data-testid={`ledger-operacion-quitar-${mov.otp_id}`}>
              quitar
            </button>
          </>
        ) : eligiendoOp ? (
          <select autoFocus defaultValue="" disabled={guardando}
            onChange={async (e) => { if (e.target.value && await post('atribuir-operacion', { operacion: e.target.value })) setEligiendoOp(false); }}
            onBlur={() => setEligiendoOp(false)}
            className="text-[10px] px-1 py-0.5 border border-violet-300 rounded bg-white dark:bg-gray-800 dark:text-gray-200 max-w-[170px]"
            data-testid={`ledger-operacion-select-${mov.otp_id}`}>
            <option value="">Elegir operación…</option>
            {(info.operaciones || []).map((o) => <option key={o.id} value={o.id}>{o.nombre}</option>)}
          </select>
        ) : (
          <button onClick={() => setEligiendoOp(true)}
            className="text-[9px] px-1.5 py-0.5 rounded bg-violet-100 hover:bg-violet-200 text-violet-800 font-medium transition-colors"
            title="Imputar esta salida al libro de UNITED o HALCON VIP+EVOLUTION (no toca saldos de clientes)"
            data-testid={`ledger-operacion-btn-${mov.otp_id}`}>
            + Operación
          </button>
        )}
      </div>

      {/* ── Detalle libre ── */}
      {editando ? (
        <div className="flex items-center gap-1">
          <input autoFocus value={nota} onChange={(e) => setNota(e.target.value)} maxLength={300}
            onKeyDown={(e) => { if (e.key === 'Enter') guardarNota(); if (e.key === 'Escape') { setNota(info.nota || ''); setEditando(false); } }}
            placeholder="Motivo de la salida…"
            className="text-[10px] px-1.5 py-0.5 border border-amber-400 rounded w-full bg-white dark:bg-gray-800 dark:text-gray-200"
            data-testid={`ledger-nota-input-${mov.otp_id}`} />
          <button onClick={guardarNota} disabled={guardando} className="text-emerald-600 hover:text-emerald-800 disabled:opacity-50"
            data-testid={`ledger-nota-save-${mov.otp_id}`}><Check className="w-3.5 h-3.5" /></button>
          <button onClick={() => { setNota(info.nota || ''); setEditando(false); }} className="text-gray-400 hover:text-gray-600"
            data-testid={`ledger-nota-cancel-${mov.otp_id}`}><X className="w-3.5 h-3.5" /></button>
        </div>
      ) : (
        <button onClick={() => setEditando(true)}
          className="flex items-start gap-1 text-left text-[10px] text-gray-600 dark:text-gray-400 hover:text-amber-700 group"
          title={info.nota_by ? `Escrito por ${info.nota_by}` : 'Agregar un detalle'}
          data-testid={`ledger-nota-btn-${mov.otp_id}`}>
          <Pencil className="w-2.5 h-2.5 mt-0.5 opacity-40 group-hover:opacity-100 shrink-0" />
          <span className={info.nota ? '' : 'italic text-gray-400'}>
            {info.nota || 'Agregar detalle'}
          </span>
        </button>
      )}
    </div>
  );
};

export default LedgerSalidaDetalle;
